<template>
  <div class="add-new-service grid grid-rows-[auto_1fr_auto] w-full h-full overflow-hidden">
    <!-- Tabs -->
    <div class="flex gap-1 p-1 bg-gray-100 rounded-xl mb-3">
      <button
        v-for="t of tabs"
        :key="t.value"
        type="button"
        :class="['flex-1 rounded-lg py-2 text-sm transition-colors',
                 activeTab === t.value ? 'bg-white text-primary font-medium shadow-sm' : 'text-gray-500']"
        @click="activeTab = t.value">
        {{ t.label }}
      </button>
    </div>

    <div class="overflow-y-auto min-h-0 pb-2">
      <!-- ============ MAIN TAB ============ -->
      <div v-show="activeTab === 'main'" class="service flex flex-col w-full">
        <div class="service-info gap-1">
          <!-- Provider assignment moved to the resources page. New services are assigned to
               all employees by default (a single employee → just them); the admin re-assigns
               per-employee from there. -->
          <div class="flex flex-col">
            <span class="mt-3 mb-1 font-medium">שם השירות</span>
            <input-text
              v-model="newService.name"
              type="text"
            />
            <small v-show="nameError" class="error">{{ nameError }}</small>
          </div>

          <div v-if="serviceType === ServiceType.REGULAR" class="flex flex-col">
            <span class="mt-3 mb-1 font-medium">קטגוריה</span>
            <AutoComplete
              v-model="newService.category"
              style="width: 100%"
              class="flex-grow"
              :suggestions="categorySuggestions"
              placeholder="למשל: שיער, ציפורניים"
              :complete-on-focus="true"
              @complete="onCategoryComplete"
            />
          </div>

          <div v-if="serviceType === ServiceType.COMPLEMENTARY" class="flex flex-col">
            <span class="mt-3 mb-1 font-medium">הצעה לשירותים</span>
            <span class="text-xs text-gray-500 mb-2">בחרו שירותים שאחריהם השירות המשלים יוצע. אם לא תבחרו, הוא יוצע ללקוחות שבחרו כל שירות.</span>
            <multi-select
              v-model="newService.attachedToServices"
              class="w-full"
              :options="regularServicesOptions"
              placeholder="ביחרו שירותים (אופציונלי)"
              :showToggleAll="false"
              optionValue="id"
              optionLabel="name" />
          </div>

          <div class="flex flex-col mt-3 mb-1">
            <div v-if="serviceType !== ServiceType.COMPLEMENTARY" class="flex w-full gap-2 mb-3">
              <button
                v-for="opt of priceTypeOptions"
                :key="opt.value"
                type="button"
                :class="['flex-1 rounded-lg border py-2 px-2 text-sm transition-colors',
                         priceType === opt.value ? 'border-primary bg-primary-50 text-primary font-medium' : 'border-gray-200 bg-white text-gray-600']"
                @click="priceType = opt.value">
                {{ opt.label }}
              </button>
            </div>

            <div v-if="priceType === PriceTypeEnum.RANGE" class="justify-center flex items-center gap-4">
              <div class="flex flex-col">
                <span class="font-medium mb-1">מחיר מינימלי</span>
                <div class="w-full relative">
                  <input-number
                    v-model="newService.price"
                    :maxFractionDigits="2"
                    :min="0"
                    locale="he-IL"
                    style="margin-bottom: 0; padding:0;"
                  />
                  <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
                </div>
              </div>
              <div class="flex flex-col">
                <span class="font-medium  mb-1">מחיר מקסימלי</span>
                <div class="w-full relative">
                  <input-number
                    v-model="newService.maxPrice"
                    :maxFractionDigits="2"
                    :min="0"
                    locale="he-IL"
                    style="margin-bottom: 0; padding:0;"
                  />
                  <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
                </div>
              </div>
            </div>
            <div v-else class="flex flex-col">
              <span class="mb-1 font-medium">{{ priceType === PriceTypeEnum.FROM ? 'מחיר התחלתי' : 'מחיר' }}</span>
              <div class="w-full relative">
                <input-number
                  v-model="newService.price"
                  :maxFractionDigits="2"
                  :min="0"
                  locale="he-IL"
                  style="margin-bottom: 0; padding:0;"
                />
                <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
              </div>
              <span v-if="priceType === PriceTypeEnum.FROM" class="text-xs text-gray-500 mt-1">
                הלקוחות יוכלו לקבוע תור אך לא לשלם דרך האתר. המחיר הסופי ייקבע בעת התשלום.
              </span>
            </div>

            <div v-if="priceType === PriceTypeEnum.FIXED" class="flex flex-col mt-3">
              <span class="mb-1 font-medium">מחיר מבצע (אופציונלי)</span>
              <div class="w-full relative">
                <input-number
                  v-model="newService.discountedPrice"
                  :maxFractionDigits="2"
                  :min="0"
                  placeholder="ללא מבצע"
                  locale="he-IL"
                  style="margin-bottom: 0; padding:0;"
                />
                <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
              </div>
              <span class="text-xs text-gray-500 mt-1">
                כשמוגדר, זהו המחיר שייגבה בפועל. המחיר נשמר בתור בעת ההזמנה — הסרת המבצע מאוחר יותר לא תשנה תורים שכבר נקבעו.
              </span>
            </div>

            <small v-show="priceError" class="error">{{ priceError }}</small>
          </div>

          <div class="flex flex-col">
            <div v-if="!isMultiSteps" class="flex flex-col">
              <span class="mt-3 mb-1 font-medium">משך השירות</span>
              <div class="w-full relative">
                <input-number
                  v-model="newService.duration"
                  :min="0"
                  :step="5"
                  class="p-inputtext-sm"
                  mode="decimal"
                  style="padding:0"
                />
                <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">דקות</span>
              </div>
            </div>
            <div v-else class="flex flex-col">
              <span class="mt-3 mb-1 font-medium">הוספת שלבים</span>
              <span class="text-xs text-gray-500 mb-2">קבעו תור בחלקים: הוסיפו הפסקות מתוכננות בין שלבי הטיפול, מושלם וטיפולים שדורשים המתנה בין שלבים כמו החלקות, צבע וכו׳.</span>
              <ServiceSteps :preDefinedSteps="service?.steps" @steps-changed="stepsChanged"/>
            </div>
            <settings-toggle
              v-if="serviceType !== ServiceType.COMPLEMENTARY"
              :status="isMultiSteps"
              title="שירות בשלבים"
              description="שירותים מרובי שלבים מאפשרים לך לקבל לקוחות נוספים בזמני ההמתנה ולנצל את הזמן ביעילות."
              @value-changed="(val) => { isMultiSteps = val }"/>
          </div>
          <small v-show="durationError" class="error">{{ durationError }}</small>

          <div class="flex flex-col">
            <span class="mt-3 mb-1 font-medium">תיאור השירות</span>
            <Textarea v-model="newService.description" :autoResize="false" rows="3"/>
            <small v-show="descriptionError" class="error">{{ descriptionError }}</small>
          </div>

          <div class="flex flex-col">
            <span class="mt-3 mb-1 font-medium">הערה ללקוח</span>
            <Textarea v-model="newService.notes" :autoResize="false" rows="2" placeholder="הערה שתוצג ללקוח בעמוד התשלום, מתחת לשירות (למשל: להגיע ללא איפור)"/>
            <small class="text-surface-400 mt-1">ההערה תוצג ללקוח בסיכום ההזמנה ובעמוד התשלום.</small>
          </div>

          <!-- Actions (edit only) -->
          <template v-if="mode === 'edit'">
            <divider :pt="{root:{style: 'margin: 1.25rem 0 0.75rem'}}" />
            <span class="font-medium mb-2">פעולות נוספות</span>

            <button
              v-if="serviceType === ServiceType.REGULAR || serviceType === ServiceType.COMPLEMENTARY"
              type="button"
              class="flex items-center gap-3 w-full p-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-right mb-2 disabled:opacity-60"
              :disabled="isConvertLoading"
              @click="convertServiceType">
              <i :class="['text-lg text-primary', isConvertLoading ? 'pi pi-spinner pi-spin' : 'pi pi-sync']" />
              <div class="flex-1">
                <div class="font-medium text-sm">{{ serviceType === ServiceType.COMPLEMENTARY ? 'המרה לשירות רגיל' : 'המרה לשירות משלים' }}</div>
                <div class="text-xs text-gray-500">
                  {{ serviceType === ServiceType.COMPLEMENTARY
                    ? 'הפיכה לשירות עצמאי שלקוחות יכולים להזמין ישירות.'
                    : 'הפיכה לתוספת שמוצעת ללקוחות אחרי שבחרו שירות ראשי.' }}
                </div>
              </div>
            </button>

            <button
              type="button"
              class="flex items-center gap-3 w-full p-3 rounded-xl border transition-colors text-right disabled:opacity-60"
              :class="newService.visible ? 'border-red-200 hover:bg-red-50' : 'border-green-200 hover:bg-green-50'"
              :disabled="isRemoveCtaLoading"
              @click="removeService">
              <i :class="['text-lg', isRemoveCtaLoading ? 'pi pi-spinner pi-spin text-gray-500' : (newService.visible ? 'pi pi-ban text-red-500' : 'pi pi-refresh text-green-600')]" />
              <div class="flex-1">
                <div class="font-medium text-sm" :class="newService.visible ? 'text-red-600' : 'text-green-700'">
                  {{ newService.visible ? 'ביטול שירות' : 'החזרת שירות' }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ newService.visible
                    ? 'השירות יוסתר מהלקוחות אך לא יימחק לצמיתות.'
                    : 'השירות יוצג שוב ללקוחות.' }}
                </div>
              </div>
            </button>
          </template>
        </div>
      </div>

      <!-- ============ ADVANCED TAB ============ -->
      <div v-show="activeTab === 'advanced'" class="service flex flex-col w-full">
        <div class="flex flex-col">
          <span class="mb-1 font-medium">צבע השירות</span>
          <span class="text-xs text-gray-500 mb-2">הצבע ישמש לתורים ביומן. השאירו ריק לצבע ברירת המחדל.</span>
          <div class="flex items-center gap-2 flex-nowrap overflow-x-auto p-2">
            <button
              v-for="color of colorPalette"
              :key="color"
              type="button"
              :title="color"
              :class="['w-8 h-8 flex-none rounded-full border-2 transition-all',
                       newService.color === color ? 'border-gray-800 scale-110' : 'border-gray-200']"
              :style="{ backgroundColor: color }"
              @click="newService.color = color"
            />
            <button
              type="button"
              :class="['w-8 h-8 flex-none rounded-full border-2 flex items-center justify-center text-xs text-gray-500',
                       !newService.color ? 'border-gray-800 bg-gray-100' : 'border-gray-200 bg-white']"
              title="ברירת מחדל"
              @click="newService.color = undefined"
            >
              <i class="pi pi-times" style="font-size: 0.7rem" />
            </button>
          </div>
        </div>

        <settings-toggle
          class="mt-4"
          v-if="serviceType === ServiceType.REGULAR"
          :status="newService.selectedClientsOnly ?? false"
          title="שירות ללקוחות נבחרים בלבד"
          description="שירות שתרצו לספק ללקוחות ספציפיים, ושיהיה מוסתר לשאר הלקוחות. את השירות תוכלו לאפשר ללקוחות בעמוד האישי שלהם, והם יראו אותו באתר לאחר שיתחברו."
          @value-changed="(val) => {newService.selectedClientsOnly = val}"/>

        <settings-toggle
          class="mt-3"
          v-if="showExemptFromAdvancePayment"
          :status="newService.exemptFromAdvancePayment ?? false"
          title="פטור מתשלום מקדמה"
          description="לקוחות יוכלו לקבוע תור לשירות זה ללא תשלום מראש"
          @value-changed="(val) => {newService.exemptFromAdvancePayment = val}"/>

        <settings-toggle
          class="mt-3"
          v-if="serviceType !== ServiceType.COMPLEMENTARY"
          :status="newService.limitedHours ?? false"
          title="הגבלת שעות לשירות"
          description="הגבילו את השעות בהן ניתן לקבוע תור לשירות זה (למשל רק בבקרים). ההגבלה חלה בנוסף לשעות הפעילות הרגילות."
          @value-changed="onLimitedHoursChanged"/>

        <div v-if="newService.limitedHours && newService.availabilityHours" class="flex flex-col mt-2">
          <ServiceAvailabilityEditor :availability="newService.availabilityHours" />
        </div>

        <div v-if="serviceType === ServiceType.REGULAR" class="flex flex-col mt-4 p-3 rounded-lg border border-gray-200">
          <ServiceChecklistEditor :checklist="newService.taskChecklist!" />
        </div>

        <div class="flex flex-col mt-4">
          <div class="flex items-center justify-between mb-1">
            <span class="font-medium">תמונת השירות</span>
            <button
              type="button"
              class="flex items-center gap-1 text-sm text-primary"
              @click="openImagePicker">
              <i class="pi pi-images" style="font-size: 0.85rem" />
              <span>תמונות מוצעות</span>
            </button>
          </div>
          <image-upload
            key="service-image-upload"
            :uploadFunction="onSelectedImage"
            :imageUrl="newService?.imageUrl"
            origin="service"
            @remove="newService.imageUrl = undefined"
          />
        </div>
      </div>
    </div>

    <div class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <!-- Never disabled: an invalid field lives on the details tab, so a dead button here
           reads as "the drawer is broken". Clicking validates, surfaces the errors and
           switches to the tab holding them. -->
      <drawer-footer
        :approveLoading="loading"
        @approve-click="clickedAddService"
        @cancel-click="$emit('close')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Days, IAvailability, IService, IServiceStep, PriceTypeEnum, ServiceType } from '@torim-app/types';
import ServiceSteps from '../components/ServiceSteps.vue';
import ServiceAvailabilityEditor from '../components/ServiceAvailabilityEditor.vue';
import ServiceChecklistEditor from '../pages/tasks/ServiceChecklistEditor.vue';

import { computed, PropType, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminStore } from '@app/stores';
import { ServicesApi } from '@app/api/admin/services-api';
import { useConfirm } from 'primevue/useconfirm';
import { useDrawer } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';
import ServiceImagePicker from './ServiceImagePicker.vue';

const toast = useToast();
const adminStore = useAdminStore();
const confirm = useConfirm();
const { openDrawer, closeDrawer } = useDrawer();

const emit = defineEmits(['close']);
const props = defineProps({
  service: {
    type: Object as PropType<IService>,
    required: false,
    default: undefined
  },
  mode: {
    type: String as PropType<'edit' | 'create'>,
    required: true
  },
  serviceType: {
    type: String as PropType<ServiceType>,
    required: true
  }
});

type ServiceTab = 'main' | 'advanced';
const tabs: Array<{ value: ServiceTab; label: string }> = [
  { value: 'main', label: 'פרטים' },
  { value: 'advanced', label: 'הגדרות מתקדמות' },
];
const activeTab = ref<ServiceTab>('main');

const initialPriceType = (() => {
  if (!props.service) return PriceTypeEnum.FIXED;
  if (props.service.priceType) return props.service.priceType;
  return props.service.maxPrice && props.service.maxPrice !== props.service.price
    ? PriceTypeEnum.RANGE
    : PriceTypeEnum.FIXED;
})();
const priceType = ref<PriceTypeEnum>(initialPriceType);
const priceTypeOptions: Array<{ value: PriceTypeEnum; label: string }> = [
  { value: PriceTypeEnum.FIXED, label: 'מחיר קבוע' },
  { value: PriceTypeEnum.RANGE, label: 'טווח מחירים' },
  { value: PriceTypeEnum.FROM, label: 'החל מ-' },
];
const colorPalette = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6', '#3B82F6', '#8864EE', '#EC4899'];
// A service counts as multi-step only when it has at least one step with a real duration.
// Leftover placeholder steps (created by toggling the switch on and off again) used to make
// the form think the service was multi-step and block saving forever.
const hasUsableSteps = (steps?: Array<IServiceStep>) => !!steps?.some(step => (step?.duration ?? 0) > 0);
const isMultiSteps = ref(hasUsableSteps(props.service?.steps));

// Work on a clone so we never mutate the store object directly, and so the form is fully
// populated from the (current) service on every open — important after a type conversion.
const cloneService = (s?: IService): IService =>
  s
    ? (JSON.parse(JSON.stringify(s)) as IService)
    : (<IService>{ providingClient: [adminStore.userId], serviceType: props.serviceType, exemptFromAdvancePayment: false });
const newService = reactive<IService>(cloneService(props.service)),
  nameError = ref(),
  priceError = ref(),
  durationError = ref(),
  descriptionError = ref();
// The checklist editor edits the arrays in place, so make sure they exist.
newService.taskChecklist = {
  before: newService.taskChecklist?.before ?? [],
  after: newService.taskChecklist?.after ?? [],
};
const loading = ref<boolean>(false);
const isRemoveCtaLoading = ref(false);
const isConvertLoading = ref(false);

const buildEmptyAvailability = (): IAvailability => ({
  [Days.SUNDAY]: [],
  [Days.MONDAY]: [],
  [Days.TUESDAY]: [],
  [Days.WEDNESDAY]: [],
  [Days.THURSDAY]: [],
  [Days.FRIDAY]: [],
  [Days.SATURDAY]: []
});

// Ensure all 7 weekday keys exist so the editor can bind to them safely.
if (newService.limitedHours)
  newService.availabilityHours = { ...buildEmptyAvailability(), ...(newService.availabilityHours ?? {}) };

const onLimitedHoursChanged = (val: boolean) => {
  newService.limitedHours = val;
  if (val && !newService.availabilityHours)
    newService.availabilityHours = buildEmptyAvailability();
};

// Normalize from/to to plain time strings before persisting.
const normalizeAvailabilityForSave = (availability?: IAvailability): IAvailability | undefined => {
  if (!availability) return undefined;
  const result = buildEmptyAvailability();
  for (const day of Object.values(Days)) {
    result[day] = (availability[day] ?? []).map(range => ({
      from: typeof range.from === 'string' ? range.from : range.from?.name,
      to: typeof range.to === 'string' ? range.to : range.to?.name
    }));
  }
  return result;
};

const isMainClient = computed(() => adminStore.getMainClient);
// New services are assigned to every employee (falling back to the current user when the
// roster is empty, e.g. an employee account). The admin re-assigns per-employee from the
// resources page afterwards.
const allProviderIds = computed<string[]>(() => {
  const ids = (adminStore.getProviders ?? []).map(p => p.userId);
  return ids.length ? ids : (adminStore.userId ? [adminStore.userId] : []);
});
const isDurationValid = computed<boolean>(() => {
  if (isMultiSteps.value) {
    return !!newService.steps && newService.steps.length > 0 && newService.steps.every(x => x.duration && x.duration > 0);
  } else {
    return Number.isInteger(newService.duration) && (newService.duration ?? 0) >= 0;
  }
});
// A stepped service has to occupy the provider at some point. When every step is a waiting
// step the appointment reserves nobody, so the scheduler reads the slot as free and lets
// another booking — or a customer booking over a manual block — land on top of it.
const hasActiveStep = computed<boolean>(() => !!newService.steps?.some(step => step.requiresProvider));
const showExemptFromAdvancePayment = computed(() =>
  (adminStore.getBusinessSiteData?.hasPayments ?? false) &&
  adminStore.getBusinessSiteData?.configurations?.schedulePaymentMode !== 'none'
);

const allCategories = computed<string[]>(() => {
  const set = new Set<string>();
  for (const s of adminStore.getRegularServices ?? [])
    if (s.category) set.add(s.category);
  return Array.from(set);
});
const regularServicesOptions = computed<Array<Pick<IService, 'id' | 'name'>>>(() =>
  (adminStore.getRegularServices ?? []).map(s => ({ id: s.id, name: s.name }))
);
const categorySuggestions = ref<string[]>([]);
const onCategoryComplete = (event: { query: string }) => {
  const q = (event.query ?? '').trim();
  if (!q) {
    categorySuggestions.value = allCategories.value;
    return;
  }
  const matches = allCategories.value.filter(c => c.toLowerCase().includes(q.toLowerCase()));
  categorySuggestions.value = matches.includes(q) ? matches : [...matches, q];
};
const resetFields = () => {
  descriptionError.value = null;
  durationError.value = null;
  priceError.value = null;
  nameError.value = null;
};

const stepsChanged = (steps: Array<IServiceStep>) => {
  newService.steps = steps;
}

const validateFields = () => {
  let isValid = true;

  if (!isDurationValid.value) {
    durationError.value = isMultiSteps.value ? 'יש למלא את כל השלבים' : 'יש להזין משך שירות תקין';
    isValid = false;
  } else if (isMultiSteps.value && !hasActiveStep.value) {
    durationError.value = 'יש לסמן לפחות שלב אחד כשלב עבודה — שירות שכולו המתנה לא תופס אתכם ביומן וניתן לקבוע תור נוסף על גביו';
    isValid = false;
  }

  if (newService.price == null || newService.price < 0) {
    priceError.value = 'יש להזין מחיר תקין';
    isValid = false;
  }

  if (priceType.value === PriceTypeEnum.RANGE && (!newService.maxPrice || newService.maxPrice <= newService.price)) {
    priceError.value = 'יש להזין טווח מחירים תקין';
    isValid = false;
  }

  if (!newService.name || newService.name === '') {
    nameError.value = 'יש להזין שם עבור השירות';
    isValid = false;
  }

  // All validated fields live in the main tab — surface them there. The toast matters on
  // mobile, where the offending field can be scrolled out of view.
  if (!isValid) {
    activeTab.value = 'main';
    toast.error('יש להשלים את הפרטים המסומנים באדום');
  }

  return isValid;
};

const removeService = () => {
  const {id: serviceId, visible: isCurrentlyVisible} = newService;
  if (isCurrentlyVisible) {
    confirm.require({
      message: 'האם לבטל את השירות?',
      header: 'רק מוודאים',
      rejectLabel: 'לא',
      acceptLabel: 'כן',
      blockScroll: true,
      accept: async () => {
        try {
          isRemoveCtaLoading.value = true;
          await ServicesApi.changeServiceVisibility(serviceId, 'false');
          await adminStore.fetchBusinessData();
          toast.success('השירות בוטל בהצלחה');
        } catch (e) {
          console.error(e)
          toast.error("לא הצלחנו לבטל את השירות, אנא נסו שוב");
        } finally {
          isRemoveCtaLoading.value = false;
          emit('close');
        }
      },
    });
  } else {
    confirm.require({
      message: 'האם להציג בחזרה את השירות?',
      header: 'רק מוודאים',
      rejectLabel: 'לא',
      acceptLabel: 'כן',
      blockScroll: true,
      accept: async () => {
        try {
          isRemoveCtaLoading.value = true;
          await ServicesApi.changeServiceVisibility(serviceId, 'true');
          await adminStore.fetchBusinessData();
          toast.success('השירות הוחזר בהצלחה');
        } catch (e) {
          console.error(e)
          toast.error("לא הצלחנו לשחזר את השירות, אנא נסו שוב");
        } finally {
          isRemoveCtaLoading.value = false;
          emit('close');
        }
      },
    });
  }
}

const convertServiceType = () => {
  const target = props.serviceType === ServiceType.COMPLEMENTARY ? ServiceType.REGULAR : ServiceType.COMPLEMENTARY;
  confirm.require({
    message: target === ServiceType.COMPLEMENTARY
      ? 'להמיר את השירות לשירות משלים? הוא יוצע ללקוחות לאחר שיבחרו שירות ראשי.'
      : 'להמיר את השירות חזרה לשירות רגיל?',
    header: 'המרת שירות',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      try {
        isConvertLoading.value = true;
        trackEvent('service_type_converted', { to: target });
        await ServicesApi.convertServiceType(
          newService.id,
          target,
          target === ServiceType.COMPLEMENTARY ? [] : undefined
        );
        await adminStore.fetchBusinessData();
        toast.success('השירות הומר בהצלחה');
      } catch (e) {
        console.error(e);
        toast.error('לא הצלחנו להמיר את השירות, אנא נסו שוב');
      } finally {
        isConvertLoading.value = false;
        emit('close');
      }
    },
  });
};

const clickedAddService = async () => {
  trackEvent('service_saved', { mode: props.mode });
  if (props.mode === 'edit')
    await saveChangesInService();
  else
    await addNewService();
}

const saveChangesInService = async () => {
  // Validate before the try/finally — the finally closes the drawer, so bailing out inside
  // it would throw away everything the user just filled in.
  resetFields();
  if (!validateFields())
    return;

  try {
    loading.value = true;
    await ServicesApi.updateService(<IService>{
      name: newService.name,
      imageUrl: newService.imageUrl,
      price: newService.price,
      discountedPrice: priceType.value === PriceTypeEnum.FIXED ? newService.discountedPrice : undefined,
      description: newService.description,
      notes: newService.notes,
      // Stepped services take their length from `steps`; a leftover flat duration here would
      // disagree with the steps and mislead everything that displays it.
      duration: isMultiSteps.value ? null : newService.duration,
      id: newService.id,
      // Provider assignment is edited on the resources page, not here — preserve whatever
      // the service already had (prefer the plural virtual, fall back to the raw field).
      providingClient: newService.providingClients ?? newService.providingClient,
      maxPrice: newService.maxPrice,
      priceType: priceType.value,
      color: newService.color,
      selectedClientsOnly: newService.selectedClientsOnly ?? false,
      exemptFromAdvancePayment: newService.exemptFromAdvancePayment ?? false,
      // Clear the steps when the service isn't multi-step — otherwise placeholder steps left
      // behind by toggling the switch get persisted and break the next edit.
      steps: isMultiSteps.value ? newService.steps : [],
      category: newService.category?.trim() || undefined,
      attachedToServices: props.serviceType === ServiceType.COMPLEMENTARY ? (newService.attachedToServices ?? []) : undefined,
      limitedHours: newService.limitedHours ?? false,
      availabilityHours: newService.limitedHours ? normalizeAvailabilityForSave(newService.availabilityHours) : undefined,
      taskChecklist: newService.taskChecklist
    });
    toast.success('השירות עודכן בהצלחה');
    await adminStore.fetchBusinessData();

  } catch (e) {
    toast.error('לא הצלחנו לערוך את השירות המבוקש');
    console.error(e)
  } finally {
    loading.value = false;
    emit('close');
  }
}

const addNewService = async (): Promise<void> => {
  resetFields();
  if (!validateFields())
    return;

  try {
    loading.value = true;
    // Assign to all employees by default (backend keys the create off providingClients).
    newService.providingClients = allProviderIds.value;
    if (typeof newService.category === 'string' && !(newService.category as string)?.trim()) delete newService.category;
    newService.priceType = priceType.value;
    if (isMultiSteps.value) newService.duration = null;
    else delete newService.steps;
    if (newService.limitedHours)
      newService.availabilityHours = normalizeAvailabilityForSave(newService.availabilityHours);
    else
      delete newService.availabilityHours;
    await ServicesApi.addService(<IService>newService);
    toast.success('השירות נוסף בהצלחה :)');
    await adminStore.fetchBusinessData();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו ליצור את השירות המבוקש, אנא נסו שוב');
  } finally {
    loading.value = false;
    emit('close');
  }
};

const onSelectedImage = async (imageUrl: string) => {
  newService.imageUrl = imageUrl;
};

// The picker has its own search box, so it's usable before the service is named.
const openImagePicker = () => {
  trackEvent('service_image_picker_opened', { mode: props.mode });
  openDrawer(
    ServiceImagePicker,
    {
      serviceName: newService.name,
      onSelect: (imageUrl: string) => { newService.imageUrl = imageUrl; },
      onClose: closeDrawer,
    },
    { title: 'תמונות מוצעות' }
  );
};

watch(
  () => priceType.value,
  (val) => {
    if (val !== PriceTypeEnum.RANGE)
      delete newService.maxPrice;
  }
);
</script>

<style lang="scss">
.add-new-service {
  width: 100%;

  .service {
    display: flex;
    flex-direction: column;
    width: 100%;

    .error {
      color: red;
      margin-bottom: 0.5rem;
    }

    .section {
      font-weight: 400;
      margin-bottom: 0.25rem;
    }

    .p-inputtext,
    .p-inputnumber {
      width: 100%;
      margin-bottom: 0.5rem;
    }

    .service-info {
      display: flex;
      flex-direction: column;
      flex-shrink: 1;
    }

    .service-image {
      width: 100%;
      height: 6rem;
      margin-bottom: 0.5rem;

      .preview {
        display: flex;
        align-items: center;
        justify-content: space-evenly;
      }
    }
  }
}
</style>
